import { ArrowRightOutlined } from '@ant-design/icons';
import { Avatar, Button, Checkbox, Col, Flex, Row, TableProps } from 'antd';
import { SorterResult } from 'antd/es/table/interface';
import NoImage from 'assets/no-image.png';
import CustomFileList from 'components/CustomFileList';
import HeaderCard from 'components/HeaderCard';
import MultiSelect from 'components/MultiSelect';
import SearchBox from 'components/SearchBox';
import SelectComponent from 'components/Select';
import TableComponent from 'components/Table';
import TagComponent from 'components/Tags';
import RBCountryFilter, {
  useRoleBasedCountryFilter,
} from 'components/UniversalFilters/RBCountryFilter';
import useAgronomistList from 'hooks/useAgronomistList';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import StakeholderStatus from 'screens/stakeholders/components/StakeholderStatus';
import {
  getWhatsappStatus,
  STAKEHOLDER_STATUS_OPTIONS,
} from 'screens/stakeholders/helper';
import { Stakeholder } from 'types/common';
import {
  getStakeholderTypeForCountry,
  isMobileScreenSize,
  renderDate,
  validateImageURL,
} from 'utils/helpers';
import VerificationDrawer from './components/VerificationDrawer';
import { stakeholderRoleToType } from './helper';
import useMutateVerifyStakeholder from './hooks/useMutateVerifyStakeholder';
import useStakeholderDataQuery from './hooks/useStakeholderDataQuery';

const AdminReview = () => {
  // Filter states
  const [name, setName] = useState<string>();
  const [searchValue, setSearchValue] = useState('');
  const [createdBy, setCreatedBy] = useState<string[]>();
  const [role, setRole] = useState<string>();
  const [status, setStatus] = useState<string>();
  const [verificationStatus, setVerificationStatus] = useState<string>();
  const [country, setCountry] = useRoleBasedCountryFilter();
  // Table states
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sortedInfo, setSortedInfo] = useState<SorterResult<Stakeholder>>({});
  const [sort, setSort] = useState('');
  // Drawer states
  const [openVerificationDrawer, setOpenVerificationDrawer] = useState(false);
  const [selectedStakeholderId, setSelectedStakeholderId] = useState<string>();

  const { t } = useTranslation();

  const params = useMemo(
    () => ({
      size: pageSize,
      page: currentPage - 1,
      sort: sort,
      name: name,
      country: country,
      createdBy: createdBy,
      role: role,
      status: status,
      verificationStatus:
        verificationStatus !== undefined
          ? verificationStatus === 'true'
          : undefined,
    }),
    [
      pageSize,
      currentPage,
      sort,
      name,
      country,
      createdBy,
      role,
      status,
      verificationStatus,
    ],
  );

  const { data: { data: stakeholderData } = {}, isLoading } =
    useStakeholderDataQuery(params);
  const { agronomistFilterOptions, isLoading: isAgronomistLoading } =
    useAgronomistList(country, {
      role: 'srPm',
      labelKey: 'email',
      valueKey: 'email',
    });

  const { mutate, isPending: isLoadingVerify } = useMutateVerifyStakeholder();

  const handlePageChange = (page: number, pageSize: number) => {
    setCurrentPage(page);
    setPageSize(pageSize);
  };

  const handleChange: TableProps<Stakeholder>['onChange'] = (
    _,
    __,
    tableSort,
  ) => {
    const sort = tableSort as SorterResult<Stakeholder>;
    if (sort.order) {
      setSort(`${sort.columnKey},${sort.order === 'ascend' ? 'asc' : 'desc'}`);
    } else {
      setSort('');
    }
    setSortedInfo(sort);
  };

  const handleFilterChange = <T,>(setValue: (value: T) => void) => {
    return (value: T) => {
      setValue(value);
      setCurrentPage(1);
    };
  };

  const handleClickRow = (data: Stakeholder) => {
    setSelectedStakeholderId(data.id);
    setOpenVerificationDrawer(true);
  };

  const handleCloseDrawer = () => {
    setOpenVerificationDrawer(false);
    setSelectedStakeholderId(undefined);
  };

  const tableHeader: TableProps<Stakeholder>['columns'] = [
    {
      title: 'Stakeholder Name',
      dataIndex: 'name',
      key: 'name',
      onCell: (data) => ({ onClick: () => handleClickRow(data) }),
      className: 'clickable',
      render: (name, data) => (
        <Flex align="center" justify="space-between" gap={4}>
          <Flex align="center" gap={8}>
            <Avatar
              src={
                validateImageURL(data?.documentList?.[0]?.url)
                  ? data.documentList[0].url
                  : NoImage
              }
              style={{ minWidth: '32px' }}
            />
            <div className="first-column-text">{name}</div>
          </Flex>
          {!isMobileScreenSize ? (
            <Button className="table-click-indicator" type="text">
              <ArrowRightOutlined />
            </Button>
          ) : null}
        </Flex>
      ),
      fixed: 'left',
      width: 200,
    },
    {
      title: 'Role',
      dataIndex: 'role',
      key: 'role',
      sorter: true,
      sortOrder: sortedInfo.columnKey === 'role' ? sortedInfo.order : undefined,
      render(value, record, index) {
        const roleType = value === 'Paddy Buyer' ? 'PB' : value;

        return roleType === 'PB'
          ? getStakeholderTypeForCountry(country, 'PB')
          : roleType;
      },
      width: 188,
    },
    {
      title: t('common.filters.status'),
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status) => <StakeholderStatus status={status} />,
      sorter: true,
      sortOrder:
        sortedInfo.columnKey === 'status' ? sortedInfo.order : undefined,
    },
    {
      title: 'National ID Number',
      dataIndex: 'nationalIdNumber',
      key: 'nationalIdNumber',
      render: (nationalIdNumber) => nationalIdNumber || 'N/A',
      width: 200,
    },
    {
      title: 'National ID Picture',
      dataIndex: 'nationalIdDocs',
      key: 'nationalIdDocs',
      render: (docs) =>
        docs?.length ? (
          <CustomFileList
            images={docs?.map((img: { url: string }) => img.url)}
          />
        ) : (
          'N/A'
        ),
      width: 250,
    },
    {
      title: 'Contract Picture',
      dataIndex: 'contractDocs',
      key: 'contractDocs',
      render: (docs) =>
        docs?.length ? (
          <CustomFileList
            images={docs?.map((img: { url: string }) => img.url)}
          />
        ) : (
          'N/A'
        ),
      width: 250,
    },
    {
      title: t('farmerDetailsPage.phoneNumber'),
      dataIndex: 'phoneNumber',
      key: 'phoneNumber',
      width: 160,
    },
    {
      title: 'Verification Status',
      dataIndex: 'waVerifiedStatus',
      key: 'waVerifiedStatus',
      render: (status) => {
        const waStatus = getWhatsappStatus(status);
        return (
          <TagComponent label={waStatus.label} type={waStatus.statusColor} />
        );
      },
      width: 170,
    },
    {
      title: t('common.createdDate'),
      dataIndex: 'createdDate',
      key: 'createdDate',
      render: (createdDate) => renderDate(createdDate),
      width: 180,
    },
    {
      title: t('addFgForm.createdBy'),
      dataIndex: 'createdBy',
      key: 'createdBy',
      width: 250,
    },
    {
      title: 'Data Verification',
      dataIndex: 'dataVerifiedStatus',
      key: 'dataVerifiedStatus',
      width: 120,
      fixed: 'right',
      render: (dataVerifiedStatus, data) => {
        return (
          <Flex gap={8} align="center" justify="center">
            <Checkbox
              checked={dataVerifiedStatus}
              onChange={() => {
                mutate({
                  stakeholderId: data.id,
                  stakeholderType: stakeholderRoleToType(data.role),
                  dataVerifiedStatus: !dataVerifiedStatus,
                });
              }}
            />
          </Flex>
        );
      },
    },
  ];

  const getRoleOptions = (country: string) => {
    return [
      {
        value: 'Paddy Buyer',
        label: getStakeholderTypeForCountry(country, 'PB'),
      },
      { value: 'Farmer Group Leader', label: 'Farmer Group Leader' },
      { value: 'Machine Owner', label: 'Machine Owner' },
    ];
  };

  return (
    <div className="dashboard-container">
      <HeaderCard
        headerLabel="Stakeholder Data Review"
        headerInfo="Check data validation of our stakeholders here"
        filters={
          <Flex gap={20} align="center" wrap="wrap">
            <RBCountryFilter
              className="select-box"
              selectedCountry={country}
              onChange={setCountry}
            />
          </Flex>
        }
      />

      <Row>
        <Col span={24} className="table-container">
          <Row className="filters">
            <SearchBox
              placeholder="Search stakeholder by name"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              onSearch={(value) => {
                handleFilterChange(setName)(value);
                setSearchValue(value);
              }}
            />
            <MultiSelect
              allowClear
              loading={isAgronomistLoading}
              label="Created By"
              options={agronomistFilterOptions}
              value={createdBy}
              onChange={handleFilterChange(setCreatedBy)}
            />

            <SelectComponent
              label="Role"
              placeholder="Select Role"
              options={getRoleOptions(country)}
              value={role}
              onChange={handleFilterChange(setRole)}
              allowClear
            />

            <SelectComponent
              label="Status"
              placeholder="Select Status"
              options={STAKEHOLDER_STATUS_OPTIONS}
              value={status}
              onChange={handleFilterChange(setStatus)}
              allowClear
            />

            <SelectComponent
              label="Data Verification"
              placeholder="Select Status"
              options={[
                { value: 'true', label: 'Verified' },
                { value: 'false', label: 'Unverified' },
              ]}
              value={verificationStatus}
              onChange={handleFilterChange(setVerificationStatus)}
              allowClear
            />
          </Row>
          <TableComponent<Stakeholder>
            tableColumns={stakeholderData?.content}
            tableHeader={tableHeader}
            loading={isLoading || isLoadingVerify}
            onChange={handleChange}
            pagination={{
              total: stakeholderData?.totalElements ?? 0,
              current: currentPage,
              pageSize: pageSize,
              onChange: handlePageChange,
            }}
            highlightColor="gray"
            highlighted={selectedStakeholderId}
          />
        </Col>
      </Row>
      <VerificationDrawer
        country={country}
        dataId={selectedStakeholderId}
        open={openVerificationDrawer}
        onClose={handleCloseDrawer}
        params={params}
      />
    </div>
  );
};

export default AdminReview;
